import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Calendar as CalendarIcon, Cake, CalendarX, Loader2, History } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import { EVENT_COLORS } from './constants';
import { formatLastFirst } from '../../lib/nameFormat';
import { vacationsAPI } from '../../lib/api';

const VACATION_TYPES = new Set(['vacation_approved', 'vacation_pending']);

export const DayDetailDialog = ({
  open,
  onOpenChange,
  selectedDay,
  holidayMap,
  getSchulferienForDay,
  getEventsForDay,
  birthdaysByDay = {},
  canManageVacations = false,
  onVacationChanged,
}) => {
  const [removingId, setRemovingId] = useState(null);
  const dateStr = selectedDay ? format(selectedDay, 'yyyy-MM-dd') : null;

  const handleRemoveDay = async (event) => {
    if (!dateStr) return;
    const dayLabel = format(selectedDay, 'EEEE, dd.MM.yyyy', { locale: de });
    if (!window.confirm(`${dayLabel} aus dem Urlaub von ${formatLastFirst(event.employee)} entfernen?`)) return;
    setRemovingId(event.id);
    try {
      const res = await vacationsAPI.removeDays(event.id, [dateStr]);
      const segments = res.data?.segments ?? 0;
      if (segments === 0) {
        toast.success('Tag entfernt – der Urlaub bestand nur aus diesem Tag und wurde aufgehoben');
      } else if (segments > 1) {
        toast.success(`Tag entfernt – Urlaub in ${segments} Zeiträume aufgeteilt`);
      } else {
        toast.success('Urlaubstag entfernt');
      }
      onOpenChange(false);
      onVacationChanged && onVacationChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Entfernen des Tages');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-primary" />
            {selectedDay && format(selectedDay, 'EEEE, dd.MM.yyyy', { locale: de })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[400px] overflow-y-auto" data-testid="day-detail-dialog">
          {selectedDay && (() => {
            const holiday = holidayMap[dateStr];
            const events = getEventsForDay(selectedDay);
            const dayFerien = getSchulferienForDay(selectedDay);
            const dayBirthdays = birthdaysByDay[`${selectedDay.getDate()}-${selectedDay.getMonth() + 1}`] || [];

            return (
              <>
                {holiday && (
                  <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 text-sm font-medium text-purple-800 dark:text-purple-200">
                    {holiday}
                  </div>
                )}
                {dayBirthdays.length > 0 && (
                  <div
                    className="p-3 rounded-lg bg-pink-50 dark:bg-pink-950/30 border border-pink-200 dark:border-pink-900"
                    data-testid="day-detail-birthdays"
                  >
                    <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-pink-700 dark:text-pink-300">
                      <Cake className="h-4 w-4" />
                      Geburtstag {dayBirthdays.length === 1 ? '' : `(${dayBirthdays.length})`}
                    </div>
                    <ul className="space-y-1 text-sm text-pink-800 dark:text-pink-200">
                      {dayBirthdays.map((b) => (
                        <li key={b.id} className="flex items-center gap-2">
                          <span className="text-pink-500">🎂</span>
                          <span className="font-medium">{b.name}</span>
                          {b.position && (
                            <span className="text-xs text-pink-600/80 dark:text-pink-400/80">· {b.position}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {dayFerien.length > 0 && (
                  <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-sm text-amber-800 dark:text-amber-200">
                    {dayFerien.map((f) => (
                      <div key={`${f.id || f.name}-${f.bundesland}`} className="flex items-center gap-1.5">
                        <span className="font-medium">{f.name}</span>
                        <span className="text-xs text-amber-600 dark:text-amber-400">({f.bundesland_name || f.bundesland})</span>
                      </div>
                    ))}
                  </div>
                )}
                {events.length > 0 ? (
                  events.map(event => {
                    const colorInfo = EVENT_COLORS[event.type];
                    const isVacation = VACATION_TYPES.has(event.type);
                    return (
                      <div key={`${event.type}-${event.id}`} className="p-3 rounded-lg bg-secondary/30 flex items-start gap-3">
                        <div className={`w-1.5 self-stretch rounded-full ${colorInfo.bar} flex-shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{formatLastFirst(event.employee)}</p>
                          <p className="text-xs text-muted-foreground">
                            {colorInfo.label}{event.label && event.label !== colorInfo.label ? ` - ${event.label}` : ''}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {format(parseISO(event.start), 'dd.MM.', { locale: de })} - {format(parseISO(event.end), 'dd.MM.yyyy', { locale: de })}
                          </p>
                          {event.notes && <p className="text-xs text-muted-foreground italic mt-1">{event.notes}</p>}
                          {event.isRetroactive && (
                            <span className="inline-flex items-center gap-1 text-[11px] mt-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" data-testid="retroactive-badge">
                              <History className="h-3 w-3" /> rückwirkend erfasst
                            </span>
                          )}
                          {canManageVacations && isVacation && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 mt-2 text-destructive hover:text-destructive px-2"
                              onClick={() => handleRemoveDay(event)}
                              disabled={removingId === event.id}
                              data-testid={`remove-single-day-${event.id}`}
                            >
                              {removingId === event.id ? (
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              ) : (
                                <CalendarX className="h-3 w-3 mr-1" />
                              )}
                              Diesen Tag aus Urlaub entfernen
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <CalendarIcon className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{dayBirthdays.length > 0 ? 'Keine weiteren Abwesenheiten' : 'Keine Abwesenheiten an diesem Tag'}</p>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const CalendarLegend = () => (
  <div className="flex flex-wrap gap-4 text-sm" data-testid="team-calendar-legend">
    {Object.entries(EVENT_COLORS).map(([key, info]) => (
      <div key={key} className="flex items-center gap-2">
        <div className={`w-4 h-2.5 rounded-sm ${info.bar}`} />
        <span className="text-muted-foreground">{info.label}</span>
      </div>
    ))}
    <div className="flex items-center gap-2">
      <div className="w-4 h-2.5 rounded-sm bg-purple-300 dark:bg-purple-700" />
      <span className="text-muted-foreground">Feiertag</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-4 h-2.5 rounded-sm bg-amber-200 dark:bg-amber-700" />
      <span className="text-muted-foreground">Schulferien</span>
    </div>
    <div className="flex items-center gap-2">
      <Cake className="h-4 w-4 text-pink-500" />
      <span className="text-muted-foreground">Geburtstag</span>
    </div>
  </div>
);
